import { forwardRef } from "react";

import { Button, ButtonProps } from "../Button";
import { usePopoverContext } from "./usePopoverContext";
import { supportsPopover } from "../../internal/functions/supportsPopover";
import { PopoverLegacyClose, PopoverLegacyCloseProps } from "../PopoverLegacy";

/**
 * Props for the PopoverClose component
 * @extends ButtonProps
 */
export type PopoverCloseProps = ButtonProps;

/**
 * PopoverClose component for closing the popover.
 *
 * Features:
 * - Automatic popover closing on click
 * - Supports all Button component props and styling
 * - Controlled and uncontrolled mode support
 * - Legacy popover support with fallback
 * - Context integration with parent popover
 * - Accessibility support with proper button semantics
 * - Delayed closing for controlled mode
 *
 * @example
 * <Popover.Close>Close</Popover.Close>
 *
 * @example
 * <Popover.Close appearance="secondary" size="small">
 *   Cancel
 * </Popover.Close>
 *
 * @example
 * <Popover.Close onClick={() => console.log('Custom close handler')}>
 *   Close with custom handler
 * </Popover.Close>
 */
export const PopoverClose = forwardRef<HTMLButtonElement, PopoverCloseProps>(
  function PopoverClose(props, ref) {
    const { onClick, ...rest } = props;
    const context = usePopoverContext();

    if (!supportsPopover())
      return (
        <PopoverLegacyClose {...(props as PopoverLegacyCloseProps)} ref={ref} />
      );
    return (
      <Button
        ref={ref}
        data-anv="popover-close"
        {...rest}
        onClick={(event) => {
          onClick?.(event);
          if (context?.controlled) {
            setTimeout(() => {
              context?.onClose?.();
            }, 100);
            return;
          }
          context?.popover.close();
        }}
      />
    );
  },
);
